"use client";
import React, { createContext, useContext, ReactNode, useEffect, Suspense, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

interface LoginContextType {
  isLoginOpen: boolean;
  openLogin: () => void;
  closeLogin: () => void;
  switchToSignup: () => void;
  switchToLogin: () => void;
  switchToReferral: (email: string) => void;
}

const LoginContext = createContext<LoginContextType | undefined>(undefined);

export const useLogin = () => {
  const context = useContext(LoginContext);
  if (context === undefined) {
    throw new Error('useLogin must be used within a LoginProvider');
  }
  return context;
};

interface LoginProviderProps {
  children: ReactNode;
}

// Helper component to handle URL query parameters
const LoginUrlHandler = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    const overlayType = searchParams?.get('loginoverlay');

    if (!autoOpenedRef.current) {
      if (overlayType === 'signup') {
        router.push('/signup', { scroll: false });
        autoOpenedRef.current = true;
      } else if (overlayType === 'login') {
        router.push('/login', { scroll: false });
        autoOpenedRef.current = true;
      }
    }
  }, [searchParams, router]);

  return null;
};

export const LoginProvider: React.FC<LoginProviderProps> = ({ children }) => {
  const router = useRouter();

  const openLogin = () => {
    router.push('/login', { scroll: false });
  };

  const closeLogin = () => {
    // Handled by route navigation (back) usually
  };

  const switchToSignup = () => {
    router.push('/signup', { scroll: false });
  };

  const switchToLogin = () => {
    router.push('/login', { scroll: false });
  };

  const switchToReferral = (email: string) => {
    // Only logged for now as generic components handle this internally mostly
    // or it's part of signup flow steps
    console.log("Switch to referral requested for", email);
  };

  return (
    <LoginContext.Provider value={{
      isLoginOpen: false, // No longer tracked in provider
      openLogin,
      closeLogin,
      switchToSignup,
      switchToLogin,
      switchToReferral
    }}>
      <Suspense fallback={null}>
        <LoginUrlHandler />
      </Suspense>
      {children}
    </LoginContext.Provider>
  );
};